import React from 'react';
import UsaMap from 'components/map/UsaMap';
import { StateMetadata, CountyOption } from 'interfaces/StateMetadata';
import { SearchResult, SearchMode } from 'interfaces/SearchTypes';
import { stateNameToMapFormat } from 'utils/stateNameUtils';
import 'stylesheets/usaMap.css';

interface MapProps {
  stateMetadata: { [stateName: string]: StateMetadata };
  counties: CountyOption[];
  highlightedState: string | null;
  onStateClick: (stateName: string) => void;
  isTouchDevice: boolean;
  searchResults?: SearchResult[];
  mode?: SearchMode;
}

const MapComponent = ({
  stateMetadata,
  counties,
  highlightedState,
  onStateClick,
  isTouchDevice,
  searchResults = [],
  mode = 'search',
}: MapProps) => {
  // Get states that should be highlighted based on search results
  const getHighlightedStates = (): string[] => {
    const stateMap = new Map<string, number>(); // Track relevance score

    searchResults.forEach((result, index) => {
      let stateName = '';
      let relevanceScore = searchResults.length - index; // Higher score for earlier results

      if (result.type === 'state' && result.stateName) {
        stateName = result.stateName;
        relevanceScore += 10; // Boost state matches
      } else if (result.type === 'county' && result.stateName) {
        stateName = result.stateName;
        relevanceScore += 5; // Boost county matches
      }

      if (stateName) {
        const mapFormat = stateNameToMapFormat(stateName);
        const currentScore = stateMap.get(mapFormat) || 0;
        stateMap.set(mapFormat, Math.max(currentScore, relevanceScore));
      }
    });

    // Sort by relevance and return top states
    return Array.from(stateMap.entries())
      .sort((a, b) => b[1] - a[1]) // Sort by score descending
      .slice(0, 5) // Limit to top 5 states to avoid clutter
      .map(([state]) => state);
  };

  // Helper function to get the appropriate map title
  const getMapTitle = (): string => {
    if (mode === 'results' && searchResults.length > 0) {
      return 'Search results on map';
    }

    if (mode === 'results' && highlightedState) {
      return stateMetadata[highlightedState]?.state || highlightedState;
    }

    return `${isTouchDevice ? 'Tap' : 'Click'} a state to see counties`;
  };

  return (
    <div className="map-container">
      <h3 className="map-title">{getMapTitle()}</h3>

      <div
        className="map-click-area"
        role="img"
        aria-label="Interactive map of United States showing BallotTrax availability by state. Use the state list below for keyboard navigation."
        onClick={(e) => {
          // Handle clicks on the map container
          const target = e.target as SVGElement;
          let stateClass = '';

          if (target.classList) {
            // Look for state class names (like 'ca', 'ny', etc.)
            const classes = Array.from(target.classList);
            stateClass =
              classes.find((c) => c.length === 2 && /^[a-z]{2}$/.test(c)) || '';

            // If clicking on text label, look for sibling path element with state class
            if (
              !stateClass &&
              (classes.includes('abbreviationRegular') ||
                classes.includes('abbreviationSmall'))
            ) {
              const parentElement = target.parentElement;
              if (parentElement) {
                // Check all children of the parent group for a path with state class
                const siblings = Array.from(parentElement.children);
                for (const sibling of siblings) {
                  if (sibling.tagName === 'path' && sibling.classList) {
                    const siblingClasses = Array.from(sibling.classList);
                    stateClass =
                      siblingClasses.find(
                        (c) => c.length === 2 && /^[a-z]{2}$/.test(c),
                      ) || '';
                    if (stateClass) break;
                  }
                }
              }
            }

            if (stateClass) {
              // Convert state abbreviation to the lowercase_underscore format used in metadata
              const stateAbbrevToMetadataKey: { [key: string]: string } = {
                al: 'alabama',
                ak: 'alaska',
                az: 'arizona',
                ar: 'arkansas',
                ca: 'california',
                co: 'colorado',
                ct: 'connecticut',
                de: 'delaware',
                fl: 'florida',
                ga: 'georgia',
                hi: 'hawaii',
                id: 'idaho',
                il: 'illinois',
                in: 'indiana',
                ia: 'iowa',
                ks: 'kansas',
                ky: 'kentucky',
                la: 'louisiana',
                me: 'maine',
                md: 'maryland',
                ma: 'massachusetts',
                mi: 'michigan',
                mn: 'minnesota',
                ms: 'mississippi',
                mo: 'missouri',
                mt: 'montana',
                ne: 'nebraska',
                nv: 'nevada',
                nh: 'new_hampshire',
                nj: 'new_jersey',
                nm: 'new_mexico',
                ny: 'new_york',
                nc: 'north_carolina',
                nd: 'north_dakota',
                oh: 'ohio',
                ok: 'oklahoma',
                or: 'oregon',
                pa: 'pennsylvania',
                ri: 'rhode_island',
                sc: 'south_carolina',
                sd: 'south_dakota',
                tn: 'tennessee',
                tx: 'texas',
                ut: 'utah',
                vt: 'vermont',
                va: 'virginia',
                wa: 'washington',
                wv: 'west_virginia',
                wi: 'wisconsin',
                wy: 'wyoming',
                dc: 'dc',
              };

              const metadataKey = stateAbbrevToMetadataKey[stateClass];

              if (
                metadataKey &&
                Object.keys(stateMetadata).includes(metadataKey)
              ) {
                onStateClick(metadataKey);
              }
            }
          }
        }}
      >
        <UsaMap
          stateMetadata={stateMetadata}
          counties={counties}
          highlightedStates={getHighlightedStates()}
          highlightedState={highlightedState}
          isTouchDevice={isTouchDevice}
        >
          {null}
        </UsaMap>
      </div>

      <div className="map-legend">
        <div className="legend-item">
          <div className="legend-color state-support"></div>
          <span className="legend-text">State Support</span>
        </div>
        <div className="legend-item">
          <div className="legend-color county-support"></div>
          <span className="legend-text">County Support</span>
        </div>
        <div className="legend-item">
          <div className="legend-color not-available"></div>
          <span className="legend-text">Not Available</span>
        </div>
      </div>
    </div>
  );
};

export default MapComponent;
